import { AIService } from './interface';
import {
  AIBrief,
  AIActionProposal,
  CaseStudy,
  CompanyInfo,
  Customer,
  Invoice,
  LaborRole,
  LaborStep,
  LeadScore,
  MaintenanceChecklist,
  MaterialLine,
  OutreachDraft,
  PriceListItem,
  ProjectFinancialHealth,
  ProposalSettings,
  Quote,
  RFQDraft,
  ServiceReport,
  SocialMediaDraft,
} from '../../types';
import { callAIProxy, isAIProxyAvailable } from './proxyClient';
import { generateId } from '../../utils/idGenerator';
import { getTodayLocalDate } from '../../utils/scheduleDates';

const DEFAULT_OPENAI_MODEL = 'gpt-4o';

export class OpenAIProvider implements AIService {
  private model: string = DEFAULT_OPENAI_MODEL;

  /**
   * Set the model to use for API calls
   */
  setModel(model: string): void {
    this.model = model;
  }

  /**
   * Get the current model
   */
  getModel(): string {
    return this.model;
  }

  private buildMessages(systemPrompt: string, userPrompt: string) {
    return [
      { role: 'system' as const, content: systemPrompt },
      { role: 'user' as const, content: userPrompt },
    ];
  }

  private async callOpenAI(messages: any[], jsonSchemaName?: string, jsonSchema?: any): Promise<any> {
    if (!isAIProxyAvailable()) {
      throw new Error('AI proxy not configured. Set VITE_AI_WORKER_URL and Workers secrets.');
    }

    try {
      const body: any = {
        model: this.model,
        messages,
      };

      if (jsonSchema) {
        body.response_format = { type: 'json_schema', json_schema: { name: jsonSchemaName, schema: jsonSchema } };
      } else if (jsonSchemaName === 'json_object') {
        body.response_format = { type: 'json_object' };
      }

      const response = await callAIProxy('openai', 'chat', body);

      if (response) {
        if (response.choices && Array.isArray(response.choices) && response.choices[0]?.message?.content) {
          const content = response.choices[0].message.content;
          if (jsonSchema || jsonSchemaName === 'json_object') {
            try {
              return JSON.parse(content);
            } catch {
              return content;
            }
          }
          return content;
        }
        return response;
      }
    } catch (error) {
      console.error('AI proxy failed:', error);
      throw new Error('AI proxy failed. Please check Workers secrets and deployment.');
    }

    throw new Error('AI proxy returned an empty response.');
  }

  private async callOpenAIJSON<T>(
    systemPrompt: string,
    userPrompt: string,
    schemaName: string,
    schema: any,
    fallback: T
  ): Promise<T> {
    try {
      const result = await this.callOpenAI(this.buildMessages(systemPrompt, userPrompt), schemaName, schema);
      if (typeof result === 'string') {
        try {
          return JSON.parse(result) as T;
        } catch {
          return fallback;
        }
      }
      return (result ?? fallback) as T;
    } catch (error) {
      console.error('OpenAI JSON call failed:', error);
      return fallback;
    }
  }

  private normalizeActionProposals(actions: any[]): AIActionProposal[] {
    if (!Array.isArray(actions)) return [];

    return actions.map(action => ({
      id: action.id || generateId(),
      type: action.type || 'GENERAL_ADVICE',
      title: action.title || 'Action item',
      description: action.description || '',
      data: action.data,
      status: action.status || 'PENDING',
      reasoning: action.reasoning,
    }));
  }

  private normalizeParsedDailyLog(result: any): any {
    const today = getTodayLocalDate();
    return {
      date: result?.date || today,
      location: result?.location,
      workCompleted: Array.isArray(result?.workCompleted) ? result.workCompleted : [],
      projectsTouched: Array.isArray(result?.projectsTouched) ? result.projectsTouched : [],
      timeSummary: result?.timeSummary || {},
      tools: Array.isArray(result?.tools) ? result.tools : [],
      issues: Array.isArray(result?.issues) ? result.issues : [],
      notes: Array.isArray(result?.notes) ? result.notes : [],
      followUps: Array.isArray(result?.followUps) ? result.followUps : [],
      tomorrowPriorities: Array.isArray(result?.tomorrowPriorities) ? result.tomorrowPriorities : [],
      actions: Array.isArray(result?.actions) ? result.actions : [],
    };
  }

  async generateScopeSuggestions(textContext: string, existingRoles: LaborRole[]): Promise<any> {
    const roleNames = existingRoles.map(r => r.name).join(', ');

    let learningContext = '';
    try {
      const { AILearningService } = await import('../aiLearningService');
      learningContext = AILearningService.buildLearningContext(
        'SCOPE_EXTRACTION',
        textContext.substring(0, 200),
        { roleNames, textLength: textContext.length }
      );
    } catch (e) {
      // Ignore learning context if unavailable
    }

    let prompt = '';
    try {
      const { PromptManager } = await import('../prompts/promptManager');
      const rendered = await PromptManager.getPrompt('SCOPE_EXTRACTION', 'OPENAI', {
        roleNames,
        textContext,
        learningContext: learningContext || undefined,
      });
      if (rendered) {
        prompt = rendered.userPrompt;
      }
    } catch (e) {
      console.warn('PromptManager not available, using hardcoded prompt:', e);
    }

    if (!prompt) {
      prompt = `
You are an expert industrial estimator. Analyze the following bid document text.
Extract distinct Scope Items (Bid Items).
For each item, suggest a title, a brief description, and a list of labor steps needed.
Available Labor Roles: ${roleNames}.
${learningContext ? `\n\n${learningContext}\n\n` : ''}
Return JSON array.

Bid Document Text:
${textContext}
`;
    }

    const schema = {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          code: { type: 'string' },
          title: { type: 'string' },
          description: { type: 'string' },
          suggestedSteps: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                description: { type: 'string' },
                role: { type: 'string' },
                hours: { type: 'number' },
              },
            },
          },
        },
      },
    };

    return this.callOpenAIJSON('You are an expert industrial estimator.', prompt, 'scope_suggestions', schema, []);
  }

  async generateScopeSuggestionsFromDocument(base64Data: string, mimeType: string, existingRoles: LaborRole[]): Promise<any> {
    const roleNames = existingRoles.map(r => r.name).join(', ');
    const prompt = `Extract distinct Scope Items (Bid Items) from this document. Suggest title, description, and labor steps. Available Labor Roles: ${roleNames}. Return JSON.\n\nDocument (${mimeType}, base64):\n${base64Data}`;

    const schema = {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          code: { type: 'string' },
          title: { type: 'string' },
          description: { type: 'string' },
          suggestedSteps: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                description: { type: 'string' },
                role: { type: 'string' },
                hours: { type: 'number' },
              },
            },
          },
        },
      },
    };

    return this.callOpenAIJSON('You are an expert industrial estimator.', prompt, 'scope_suggestions_doc', schema, []);
  }

  async suggestMaterialLines(
    title: string,
    scopeNotes: string,
    laborSteps: LaborStep[],
    currentMaterials: MaterialLine[],
    priceList: PriceListItem[]
  ): Promise<any[]> {
    const prompt = `
You are a construction material estimator. Suggest materials based on scope.

Title: ${title}
Scope: ${scopeNotes}
Labor: ${laborSteps.map(s => s.description).join(', ')}
Current Materials: ${currentMaterials.map(m => m.description).join(', ')}
Price List (sample): ${priceList.slice(0, 50).map(p => `${p.modelNumber || 'N/A'} ${p.description}`).join('; ')}

Return JSON array of materials with: description, modelNumber, vendor, qty, estimatedUnitCost, leadTimeWeeks.
`;

    const schema = {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          description: { type: 'string' },
          modelNumber: { type: 'string' },
          vendor: { type: 'string' },
          qty: { type: 'number' },
          estimatedUnitCost: { type: 'number' },
          leadTimeWeeks: { type: 'number' },
        },
      },
    };

    return this.callOpenAIJSON('You are a construction material estimator.', prompt, 'material_suggestions', schema, []);
  }

  async generateLaborSteps(itemTitle: string, itemDescription: string, existingRoles: LaborRole[]): Promise<any> {
    const prompt = `
Generate labor steps for:
Title: ${itemTitle}
Description: ${itemDescription}

Roles: ${existingRoles.map(r => `${r.name} ($${r.rate}/hr)`).join(', ')}

Return JSON array of steps with: description, role, hours.
`;

    const schema = {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          description: { type: 'string' },
          role: { type: 'string' },
          hours: { type: 'number' },
        },
      },
    };

    return this.callOpenAIJSON('You are a construction labor estimator.', prompt, 'labor_steps', schema, []);
  }

  async generateScopeFromLabor(
    title: string,
    laborSteps: LaborStep[],
    materialLines: MaterialLine[],
    existingRoles: LaborRole[],
    style: 'BULLET' | 'BRIEF' | 'DETAILED'
  ): Promise<string> {
    let styleInstructions = '';
    try {
      const { WritingStyleService } = await import('../prompts/writingStyleService');
      styleInstructions = await WritingStyleService.getInstructionsFromLegacyName(style, 'SCOPE_WRITING');
    } catch (e) {
      const fallbacks: Record<string, string> = {
        BRIEF: 'Write a brief, concise description.',
        BULLET: 'Write as a bullet-point list.',
        DETAILED: 'Write a detailed, comprehensive description.',
      };
      styleInstructions = fallbacks[style] || fallbacks.BRIEF;
    }

    const systemPrompt = 'You are a technical writer for construction proposals. Write clear, professional scope descriptions.';

    const laborDescriptions = laborSteps.map(s => s.description).filter(Boolean).join(', ');
    const materialDescriptions = materialLines.map(m => m.description).filter(Boolean).join(', ');

    const userMessage = `Write a scope description for the following work item:

Title: ${title}
${laborDescriptions ? `Labor Steps: ${laborDescriptions}` : ''}
${materialDescriptions ? `Materials: ${materialDescriptions}` : ''}

Style Instructions: ${styleInstructions}

Return plain text only, no markdown formatting.`;

    try {
      const response = await this.callOpenAI([
        { role: 'system' as const, content: systemPrompt },
        { role: 'user' as const, content: userMessage },
      ]);
      return typeof response === 'string' ? response : (response?.choices?.[0]?.message?.content || '');
    } catch (error) {
      console.error('OpenAI generateScopeFromLabor error:', error);
      throw error;
    }
  }

  async checkScopeConsistency(
    title: string,
    scopeNotes: string,
    laborSteps: LaborStep[],
    materialLines: MaterialLine[],
    existingRoles: LaborRole[]
  ): Promise<any[]> {
    const prompt = `Check consistency:\nTitle: ${title}\nScope: ${scopeNotes}\nLabor: ${laborSteps.map(s => s.description).join(', ')}\nMaterials: ${materialLines.map(m => m.description).join(', ')}\n\nRespond with JSON array of issues.`;

    const schema = {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          type: { type: 'string', enum: ['MISSING_COST', 'MISSING_SCOPE', 'OTHER'] },
          message: { type: 'string' },
        },
      },
    };

    return this.callOpenAIJSON('You are a QA reviewer for construction estimates.', prompt, 'scope_consistency', schema, []);
  }

  async parseBidFormDocument(base64Data: string, mimeType: string): Promise<any> {
    const prompt = `Parse this bid form document and extract key line items. Return JSON array of {id, label}. Document (${mimeType}, base64):\n${base64Data}`;

    const schema = {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          label: { type: 'string' },
        },
      },
    };

    return this.callOpenAIJSON('You are a document parser. Extract bid form lines.', prompt, 'bid_form', schema, []);
  }

  async parseVendorQuote(base64Data: string, mimeType: string): Promise<any> {
    const prompt = `Extract vendor quote line items (Model, Desc, Qty, UnitPrice, LeadTimeWeeks). Return JSON with vendorName and items. Document (${mimeType}, base64):\n${base64Data}`;

    const schema = {
      type: 'object',
      properties: {
        vendorName: { type: 'string' },
        items: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              modelNumber: { type: 'string' },
              description: { type: 'string' },
              qty: { type: 'number' },
              unitPrice: { type: 'number' },
              leadTimeWeeks: { type: 'number' },
            },
          },
        },
      },
    };

    return this.callOpenAIJSON('You are a document parser. Extract vendor quote line items.', prompt, 'vendor_quote', schema, { items: [] });
  }

  async parseSupplierInvoice(base64Data: string, mimeType: string): Promise<any> {
    const prompt = `Extract supplier invoice lines (Vendor, Inv#, Date, Items: Model, Desc, Qty, Cost). Return JSON. Document (${mimeType}, base64):\n${base64Data}`;

    const schema = {
      type: 'object',
      properties: {
        vendorName: { type: 'string' },
        invoiceNumber: { type: 'string' },
        invoiceDate: { type: 'string' },
        items: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              modelNumber: { type: 'string' },
              description: { type: 'string' },
              qty: { type: 'number' },
              unitCost: { type: 'number' },
              totalCost: { type: 'number' },
            },
          },
        },
      },
    };

    return this.callOpenAIJSON('You are a document parser. Extract supplier invoice data.', prompt, 'supplier_invoice', schema, { items: [] });
  }

  async parsePriceListDocument(base64Data: string, mimeType: string): Promise<any> {
    const prompt = `Extract price list items. Return JSON with vendorName and items (modelNumber, description, unitCost). Document (${mimeType}, base64):\n${base64Data}`;

    const schema = {
      type: 'object',
      properties: {
        vendorName: { type: 'string' },
        items: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              modelNumber: { type: 'string' },
              description: { type: 'string' },
              unitCost: { type: 'number' },
            },
          },
        },
      },
    };

    return this.callOpenAIJSON('You are a document parser. Extract price list items.', prompt, 'price_list', schema, { items: [] });
  }

  async analyzeQuoteRisks(quote: Quote): Promise<any> {
    const summary = JSON.stringify({
      project: quote.projectName,
      items: quote.bidItems.map(i => ({ title: i.title, notes: i.notes, steps: i.laborSteps.length })),
    });

    const prompt = `Analyze risks for this quote: ${summary}. Return JSON list of risks.`;

    const schema = {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          severity: { type: 'string', enum: ['HIGH', 'MEDIUM', 'LOW'] },
          title: { type: 'string' },
          description: { type: 'string' },
          recommendation: { type: 'string' },
        },
      },
    };

    return this.callOpenAIJSON('You are a risk analyst for construction estimates.', prompt, 'quote_risks', schema, []);
  }

  async generateMorningBrief(context: string): Promise<AIBrief> {
    const schema = {
      type: 'object',
      properties: {
        date: { type: 'string' },
        topPriorities: {
          type: 'array',
          items: { type: 'string' },
        },
        upcomingDeadlines: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              title: { type: 'string' },
              date: { type: 'string' },
              daysLeft: { type: 'number' },
            },
            required: ['title', 'date', 'daysLeft'],
          },
        },
        risks: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              severity: { type: 'string', enum: ['HIGH', 'MEDIUM', 'LOW'] },
              message: { type: 'string' },
            },
            required: ['severity', 'message'],
          },
        },
        suggestedSchedule: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              time: { type: 'string' },
              task: { type: 'string' },
            },
            required: ['time', 'task'],
          },
        },
        quickWins: {
          type: 'array',
          items: { type: 'string' },
        },
        followUps: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              contact: { type: 'string' },
              context: { type: 'string' },
              draftMessage: { type: 'string' },
            },
            required: ['contact', 'context', 'draftMessage'],
          },
        },
        healthNudges: {
          type: 'array',
          items: { type: 'string' },
        },
      },
      required: ['date', 'topPriorities', 'upcomingDeadlines', 'risks', 'suggestedSchedule', 'quickWins', 'followUps', 'healthNudges'],
      additionalProperties: false,
    };

    const prompt = `You are an expert Construction Business Coach. Digest the business data below and create a "Morning Brief".\n\nCONTEXT:\n${context}\n\nReturn JSON with the morning brief structure.`;

    try {
      const result = await this.callOpenAI([
        { role: 'system', content: 'You are an expert Construction Business Coach. Always return valid JSON matching the requested schema.' },
        { role: 'user', content: prompt },
      ], 'morning_brief', schema);

      let parsedResult = result;
      if (result && typeof result === 'object' && result.choices && Array.isArray(result.choices) && result.choices[0]?.message?.content) {
        try {
          parsedResult = JSON.parse(result.choices[0].message.content);
        } catch {
          // use raw result
        }
      }

      return {
        date: parsedResult?.date || new Date().toISOString(),
        topPriorities: Array.isArray(parsedResult?.topPriorities) ? parsedResult.topPriorities : [],
        upcomingDeadlines: Array.isArray(parsedResult?.upcomingDeadlines) ? parsedResult.upcomingDeadlines : [],
        risks: Array.isArray(parsedResult?.risks) ? parsedResult.risks : [],
        suggestedSchedule: Array.isArray(parsedResult?.suggestedSchedule) ? parsedResult.suggestedSchedule : [],
        quickWins: Array.isArray(parsedResult?.quickWins) ? parsedResult.quickWins : [],
        followUps: Array.isArray(parsedResult?.followUps) ? parsedResult.followUps : [],
        healthNudges: Array.isArray(parsedResult?.healthNudges) ? parsedResult.healthNudges : [],
      };
    } catch (error) {
      console.error('OpenAI generateMorningBrief error:', error);
      return {
        date: new Date().toISOString(),
        topPriorities: [],
        upcomingDeadlines: [],
        risks: [],
        suggestedSchedule: [],
        quickWins: [],
        followUps: [],
        healthNudges: [],
      };
    }
  }

  async parseDailyLog(logText: string, context: string): Promise<any> {
    const prompt = `Parse this daily log into structured data.\n\nDAILY LOG:\n${logText}\n\nBUSINESS CONTEXT:\n${context}\n\nReturn JSON with fields: date, location, workCompleted, projectsTouched, timeSummary, tools, issues, notes, followUps, tomorrowPriorities, actions.`;

    const schema = {
      type: 'object',
      properties: {
        date: { type: 'string' },
        location: { type: 'string' },
        workCompleted: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              description: { type: 'string' },
              hours: { type: 'number' },
              projectName: { type: 'string' },
              role: { type: 'string' },
              timeRange: { type: 'string' },
            },
          },
        },
        projectsTouched: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              name: { type: 'string' },
              type: { type: 'string' },
            },
          },
        },
        timeSummary: {
          type: 'object',
          properties: {
            totalHours: { type: 'number' },
            billableHours: { type: 'number' },
            nonBillableHours: { type: 'number' },
          },
        },
        tools: { type: 'array', items: { type: 'string' } },
        issues: { type: 'array', items: { type: 'string' } },
        notes: { type: 'array', items: { type: 'string' } },
        followUps: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              description: { type: 'string' },
              dueDate: { type: 'string' },
              priority: { type: 'string', enum: ['HIGH', 'MEDIUM', 'LOW'] },
              linkedProject: { type: 'string' },
            },
          },
        },
        tomorrowPriorities: { type: 'array', items: { type: 'string' } },
        actions: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              type: {
                type: 'string',
                enum: ['QUOTE_SENT', 'QUOTE_WON', 'INVOICE_CREATED', 'MATERIAL_ORDERED', 'CUSTOMER_CONTACTED', 'QUOTE_FINALIZED'],
              },
              entityName: { type: 'string' },
              details: { type: 'object' },
              requiresConfirmation: { type: 'boolean' },
            },
          },
        },
      },
      required: ['date', 'workCompleted', 'projectsTouched', 'followUps', 'actions'],
    };

    const result = await this.callOpenAIJSON('You are a time tracking assistant. Parse daily log entries.', prompt, 'daily_log', schema, {});
    return this.normalizeParsedDailyLog(result);
  }

  async analyzeDailyCheckIn(notes: string, context: string): Promise<AIActionProposal[]> {
    const prompt = `
I am a contractor providing a daily log.
NOTES: "${notes}"
BUSINESS CONTEXT: ${context}

Extract structured actions to organize my business.
Return JSON Action Proposals.
`;

    const schema = {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          type: {
            type: 'string',
            enum: ['CREATE_TASK', 'DRAFT_EMAIL', 'CREATE_FOLLOWUP', 'FLAG_RISK', 'GENERAL_ADVICE', 'ADD_CUSTOMER', 'CREATE_TIME_ENTRY'],
          },
          title: { type: 'string' },
          description: { type: 'string' },
          data: { type: 'object' },
          status: { type: 'string', enum: ['PENDING', 'APPROVED', 'REJECTED'] },
          reasoning: { type: 'string' },
        },
      },
    };

    const result = await this.callOpenAIJSON('You are a business assistant. Extract action items from daily notes.', prompt, 'daily_checkin', schema, []);
    return this.normalizeActionProposals(result as any[]);
  }

  async chatWithSecretary(history: { role: 'user' | 'assistant'; content: string }[], context: string, newMessage: string): Promise<string> {
    let contextData: any = {};
    try {
      contextData = JSON.parse(context);
    } catch (e) {
      // If context is not JSON, use as-is
    }

    let systemContext = `You are a helpful business assistant for a construction/electrical contractor. You help with quotes, invoices, schedules, and general business questions.\n\n`;

    if (contextData.quotes && Array.isArray(contextData.quotes)) {
      systemContext += `CURRENT QUOTES AND PROJECTS (including drafts):\n`;
      contextData.quotes.forEach((q: any) => {
        const statusLabel = q.status === 'DRAFT' ? 'DRAFT' : q.status;
        systemContext += `- ${q.clientName || 'Unknown Client'}: "${q.projectName || 'Untitled Project'}" (Status: ${statusLabel}, Value: $${q.value?.toLocaleString() || '0'}, ID: ${q.id})\n`;
      });
      systemContext += `\nWhen asked about a quote, search by client name (e.g., "Trickle Creek") or project name. Quote statuses include: DRAFT, SENT, WON, LOST, OPEN, PENDING, etc. Include ALL quotes including drafts when answering questions.\n\n`;
    }

    if (contextData.customers && Array.isArray(contextData.customers)) {
      systemContext += `CUSTOMERS:\n`;
      contextData.customers.slice(0, 20).forEach((c: any) => {
        systemContext += `- ${c.companyName || 'Unknown'} (Contact: ${c.contactName || 'N/A'})\n`;
      });
      systemContext += `\n`;
    }

    systemContext += `Today's date: ${contextData.date || new Date().toISOString().split('T')[0]}\n`;
    systemContext += `Company: ${contextData.company?.name || 'Unknown'}\n\n`;
    systemContext += `Answer questions accurately based on the quote and customer data provided above. If you cannot find specific information, say so clearly.`;

    const messages: any[] = [
      {
        role: 'system',
        content: systemContext,
      },
    ];

    history.forEach(h => {
      messages.push({
        role: h.role === 'assistant' ? 'assistant' : 'user',
        content: h.content,
      });
    });

    messages.push({ role: 'user', content: newMessage });

    const response = await this.callOpenAI(messages);
    if (typeof response === 'string') {
      return response;
    }

    if (response?.choices?.[0]?.message?.content) {
      return response.choices[0].message.content;
    }

    return String(response || '');
  }

  async generateSocialMediaDrafts(projectContext: string, companyInfo: string): Promise<SocialMediaDraft[]> {
    const prompt = `
You are a social media manager for an industrial contracting company (${companyInfo}).
Generate two distinct social media posts based on the project below.

PROJECT DATA:
${projectContext}

OUTPUT REQUIREMENTS:
1. LinkedIn: Professional, technical, B2B.
2. Facebook: Community-focused, casual, engaging.
3. Include image suggestion.

Return JSON array.
`;

    const schema = {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          platform: { type: 'string', enum: ['LINKEDIN', 'FACEBOOK', 'INSTAGRAM'] },
          content: { type: 'string' },
          hashtags: { type: 'array', items: { type: 'string' } },
          imageSuggestion: { type: 'string' },
        },
      },
    };

    return this.callOpenAIJSON('You are a social media manager for an industrial contractor.', prompt, 'social_drafts', schema, []);
  }

  async generateInvoiceDescription(notes: string[], style: 'BRIEF' | 'BULLET' | 'DETAILED'): Promise<string> {
    const notesText = notes.join('\n');
    const prompt = `Summarize the following daily work logs into a professional customer-facing invoice description of work performed.\nSTYLE: ${style}\n\nWORK LOGS:\n${notesText}\n\nOutput ONLY the description text.`;

    const response = await this.callOpenAI(this.buildMessages('You are a construction billing assistant.', prompt));
    return typeof response === 'string' ? response.trim() : String(response || '').trim();
  }

  async generateRFQDrafts(quote: Quote, materials: MaterialLine[]): Promise<RFQDraft[]> {
    const matList = materials
      .map(m => `- ${m.qty}x ${m.modelNumber || 'N/A'} - ${m.description} (Vendor: ${m.vendor || 'Any'})`)
      .join('\n');

    const prompt = `
You are a procurement agent for ${quote.clientName}'s project: ${quote.projectName}.
Draft RFQ emails for vendors based on this material list.
Group materials by their specified Vendor. If no vendor is specified, group them into a 'General Electrical Supply' bucket.

MATERIAL LIST:
${matList}

For each vendor group, write a professional email asking for pricing, lead time, and availability.
Return JSON array.
`;

    const schema = {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          vendorName: { type: 'string' },
          emailSubject: { type: 'string' },
          emailBody: { type: 'string' },
          itemCount: { type: 'number' },
        },
      },
    };

    return this.callOpenAIJSON('You are a procurement agent.', prompt, 'rfq_drafts', schema, []);
  }

  async generateProposalContent(quote: Quote, settings: ProposalSettings): Promise<string> {
    const scopeSummary = quote.bidItems.map(i => `- ${i.title}: ${i.notes}`).join('\n');
    const extraSections = settings.includeSections.join(', ');
    const customInstruction = settings.customInstructions
      ? `\nIMPORTANT CLIENT REQUIREMENTS / RFP INSTRUCTIONS:\n${settings.customInstructions}\n(You MUST prioritize these instructions over the standard structure)`
      : '';

    const prompt = `
Write a construction proposal cover letter.
Client: ${quote.clientName}
Project: ${quote.projectName}

CONFIGURATION:
- Tone: ${settings.tone}
- Length: ${settings.length}
- Focus: ${settings.focus}
- Include Sections: ${extraSections}
${customInstruction}

Scope Highlights:
${scopeSummary}

Structure:
1. Professional Greeting
2. Executive Summary
3. [If requested] Methodology
4. [If requested] Safety
5. [If requested] Bio
6. Key Exclusions
7. Call to Action (Valid until ${quote.dueDate})

Format: Plain Text (with markdown for bolding).
`;

    const response = await this.callOpenAI(this.buildMessages('You are a proposal writer for construction projects.', prompt));
    return typeof response === 'string' ? response.trim() : String(response || '').trim();
  }

  async generateOutreach(customer: Customer, companyInfo: CompanyInfo, recentWins: string[]): Promise<OutreachDraft[]> {
    const winList = recentWins.length > 0 ? recentWins.join(', ') : 'industrial and municipal projects';
    const targetAudience = companyInfo.targetAudience ? `Target Audience: ${companyInfo.targetAudience}` : '';

    const prompt = `
You are a Sales Development Rep for ${companyInfo.name}.
Target Client: ${customer.companyName}
Contact: ${customer.contactName || 'The Hiring Manager'}
Industry: ${customer.category || 'Construction/Industrial'}

Company Profile:
- Value Prop: ${companyInfo.valueProp || 'Quality industrial electrical and controls services.'}
- ${targetAudience}
- Recent Wins: ${winList}

Generate 3 distinct outreach drafts:
1. Cold Email (Formal introduction)
2. LinkedIn DM (Casual, connection request style)
3. Phone Script (For a cold call voicemail)

Return JSON array.
`;

    const schema = {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          recipientName: { type: 'string' },
          method: { type: 'string', enum: ['EMAIL', 'LINKEDIN_DM', 'PHONE_SCRIPT'] },
          subject: { type: 'string' },
          content: { type: 'string' },
        },
      },
    };

    return this.callOpenAIJSON('You are a sales development rep.', prompt, 'outreach_drafts', schema, []);
  }

  async optimizeValueProp(companyInfo: CompanyInfo, targetIndustry: string): Promise<string> {
    const prompt = `
Critique and improve this company's Value Proposition for the "${targetIndustry}" sector.

Company: ${companyInfo.name}
Current Value Prop: "${companyInfo.valueProp || 'General electrical contractor'}"
Target Audience: ${companyInfo.targetAudience || 'General'}

Goal: Make it punchy, authoritative, and specific to ${targetIndustry} pain points (e.g. downtime, compliance, speed).

Return ONLY the improved value proposition text (1-2 sentences).
`;

    const response = await this.callOpenAI(this.buildMessages('You are a marketing strategist.', prompt));
    return typeof response === 'string' ? response.trim() : String(response || '').trim();
  }

  async scoreLead(customer: Customer, companyInfo: CompanyInfo): Promise<LeadScore> {
    const prompt = `
Score this sales lead from 0 to 100 based on fit for our company.

Our Company:
- Name: ${companyInfo.name}
- Focus: ${companyInfo.targetAudience || 'Industrial Electrical'}
- Value Prop: ${companyInfo.valueProp}

The Lead:
- Name: ${customer.companyName}
- Category: ${customer.category}
- Location: ${customer.address}
- Notes: ${customer.notes}

Return JSON.
`;

    const schema = {
      type: 'object',
      properties: {
        score: { type: 'number' },
        reasoning: { type: 'string' },
        lastUpdated: { type: 'string' },
      },
    };

    const result = await this.callOpenAIJSON('You are a sales qualification assistant.', prompt, 'lead_score', schema, {
      score: 0,
      reasoning: '',
      lastUpdated: new Date().toISOString(),
    });

    return { ...result, lastUpdated: new Date().toISOString() };
  }

  async generateCaseStudy(quote: Quote, companyInfo: CompanyInfo): Promise<CaseStudy> {
    const scopeSummary = quote.bidItems.map(i => `${i.title}: ${i.notes}`).join('\n');

    const prompt = `
Write a professional case study for this completed project.

Project: ${quote.projectName}
Client: ${quote.clientName}
Scope:
${scopeSummary}

Company: ${companyInfo.name}

Output Requirements:
- Title
- Challenge
- Solution
- Outcome
- Technical Highlights (3-5)

Return JSON.
`;

    const schema = {
      type: 'object',
      properties: {
        title: { type: 'string' },
        challenge: { type: 'string' },
        solution: { type: 'string' },
        outcome: { type: 'string' },
        technicalHighlights: { type: 'array', items: { type: 'string' } },
      },
    };

    return this.callOpenAIJSON('You are a case study writer for industrial projects.', prompt, 'case_study', schema, {
      title: '',
      challenge: '',
      solution: '',
      outcome: '',
      technicalHighlights: [],
    });
  }

  async analyzeFinancialHealth(projects: { id: string; name: string; budgetHours: number; actualHours: number }[]): Promise<ProjectFinancialHealth[]> {
    const prompt = `
Analyze these projects for financial risk based on Labor Burn Rate.
Budget vs Actual Hours provided.

Projects:
${JSON.stringify(projects)}

For each project, calculate burn rate and provide a status (HEALTHY, WARNING, CRITICAL).
CRITICAL if actual > budget. WARNING if actual > 80% of budget. HEALTHY otherwise.
Provide short, actionable advice.

Return JSON array.
`;

    const schema = {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          projectId: { type: 'string' },
          projectName: { type: 'string' },
          budgetHours: { type: 'number' },
          actualHours: { type: 'number' },
          burnRate: { type: 'number' },
          status: { type: 'string', enum: ['HEALTHY', 'WARNING', 'CRITICAL'] },
          aiAdvice: { type: 'string' },
        },
      },
    };

    return this.callOpenAIJSON('You are a financial analyst for project delivery.', prompt, 'financial_health', schema, []);
  }

  async generateCollectionEmails(invoices: Invoice[], customers: Customer[], companyInfo: CompanyInfo): Promise<OutreachDraft[]> {
    const overdueList = invoices
      .map(i => {
        const client = customers.find(c => c.companyName === (i as any).clientName) || {
          companyName: 'Client',
          contactName: 'Accounts Payable',
        };
        return `- Invoice #${(i as any).number || 'N/A'} for $${(i as any).amount || (i as any).totalAmount || 0} to ${client.companyName} (Contact: ${client.contactName}), Due since ${(i as any).date || (i as any).issueDate || 'N/A'}`;
      })
      .join('\n');

    const prompt = `
You are an Accounts Receivable specialist for ${companyInfo.name}.
Write polite but firm overdue payment reminder emails for the following invoices.

OVERDUE ITEMS:
${overdueList}

For each invoice, generate one EMAIL draft.
Return JSON Array of OutreachDraft objects.
`;

    const schema = {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          recipientName: { type: 'string' },
          method: { type: 'string', enum: ['EMAIL'] },
          subject: { type: 'string' },
          content: { type: 'string' },
        },
      },
    };

    return this.callOpenAIJSON('You are an accounts receivable specialist.', prompt, 'collection_emails', schema, []);
  }

  async analyzeMaintenanceLog(logText: string, checklists: MaintenanceChecklist[]): Promise<Partial<ServiceReport>> {
    const checklistContext = checklists
      .map(c => `Checklist: "${c.name}" (ID: ${c.id}) Items: ${c.items.map(i => i.label).join(', ')}`)
      .join('\n');

    const schema = {
      type: 'object',
      properties: {
        summary: { type: 'string' },
        checklistResults: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              checklistId: { type: 'string' },
              label: { type: 'string' },
              status: { type: 'string', enum: ['PASS', 'FAIL', 'NA'] },
              notes: { type: 'string' },
            },
            required: ['label', 'status'],
            additionalProperties: false,
          },
        },
        issuesFound: { type: 'array', items: { type: 'string' } },
        recommendations: { type: 'array', items: { type: 'string' } },
      },
      required: ['summary', 'checklistResults', 'issuesFound', 'recommendations'],
      additionalProperties: false,
    };

    const prompt = `
You are a Service Manager for a water treatment plant.

DAILY LOG:
${logText}

AVAILABLE CHECKLISTS:
${checklistContext}

TASKS:
1. Map the log content to the relevant checklist items. If the log confirms an item is good, mark PASS. If broken, FAIL.
2. Extract a professional summary for the "Service Report".
3. Identify specific issues found (e.g. leaks, loose wires).
4. Suggest recommendations.
`;

    const result = await this.callOpenAI([
      { role: 'system', content: 'You are a Facility Maintenance Expert.' },
      { role: 'user', content: prompt },
    ], 'maintenance_analysis', schema);

    return result;
  }
}
